(() => {
  if (window.dartsrxCartUpsellBound) return;
  window.dartsrxCartUpsellBound = true;

  const getDrawer = () => document.querySelector('cart-drawer');

  const getSections = () => ['cart-drawer'];

  const openCartDrawer = (trigger) => {
    const drawer = getDrawer();

    if (!drawer) return false;

    if (typeof drawer.open === 'function') {
      drawer.open(trigger);
    } else {
      drawer.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
    }

    return true;
  };

  const getCartLink = (target) =>
    target?.closest?.('a#cart-icon-bubble, a.header__icon--cart, a.dartsrx-glass-header__cart') || null;

  const renderDrawer = (state) => {
    const drawer = getDrawer();

    if (drawer && typeof drawer.renderContents === 'function' && state.sections) {
      drawer.renderContents(state);
      return;
    }

    if (!openCartDrawer()) {
      window.location.href = window.routes?.cart_url || '/cart';
    }
  };

  const setMeterComplete = (complete) => {
    const drawer = getDrawer();
    const meter = drawer?.querySelector('.dartsrx-cart-meter');
    const text = meter?.querySelector('.dartsrx-cart-meter__text span:last-child');
    const progress = meter?.querySelector('.dartsrx-cart-meter__track span');

    if (!meter) return;

    meter.classList.toggle('is-complete', complete);

    if (text) {
      text.textContent = complete
        ? 'You have unlocked free shipping.'
        : 'Upgrade to subscription to unlock free shipping.';
    }

    if (progress) {
      progress.style.setProperty('--dartsrx-cart-progress', complete ? '100%' : '72%');
    }
  };

  const getCartItemRow = (line) => {
    const drawer = getDrawer();
    return drawer?.querySelector(`#CartDrawer-Item-${line}`) || null;
  };

  const applyOptimisticLine = (button) => {
    const drawer = getDrawer();
    const line = Number(button.dataset.line);
    const row = getCartItemRow(line);
    const priceText = button.dataset.priceText;
    const linePriceText = button.dataset.linePriceText;
    const totalPriceText = button.dataset.totalPriceText;
    const cadenceLabel = button.dataset.cadenceLabel;
    const changes = [];
    const setText = (element, text) => {
      if (!element || !text) return;

      changes.push([element, element.textContent]);
      element.textContent = text;
    };

    if (row) {
      const detailPrice =
        row.querySelector('.cart-item__details .cart-item__final-price.product-option') ||
        row.querySelector('.cart-item__details > .product-option');

      setText(detailPrice, priceText);

      const linePrices = [...row.querySelectorAll('.cart-item__price-wrapper .price--end')];
      setText(linePrices[linePrices.length - 1], linePriceText);

      const sellingPlanText = row.querySelector('.cart-item__details p.product-option');
      setText(sellingPlanText, cadenceLabel);
    }

    if (drawer && totalPriceText) {
      const total = drawer.querySelector('.totals__total-value');
      setText(total, totalPriceText);
    }

    return () => {
      changes.reverse().forEach(([element, text]) => {
        element.textContent = text;
      });
    };
  };

  const formatCartMoney = (cents, currency) => {
    if (!Number.isFinite(Number(cents)) || !currency) return '';

    return new Intl.NumberFormat(document.documentElement.lang || navigator.language, {
      style: 'currency',
      currency,
    }).format(Number(cents) / 100);
  };

  const renumberCartRows = (drawer) => {
    const rows = drawer?.querySelectorAll('#CartDrawer-CartItems tbody > .cart-item') || [];

    rows.forEach((row, index) => {
      const line = index + 1;
      const variantId = row.dataset.variantId || '';
      const quantityInput = row.querySelector('.quantity__input');
      const removeButton = row.querySelector('cart-remove-button');
      const removeControl = removeButton?.querySelector('.cart-remove-button');
      const lineError = row.querySelector('.cart-item__error');

      row.id = `CartDrawer-Item-${line}`;

      if (quantityInput) {
        quantityInput.id = `Drawer-quantity-${line}`;
        quantityInput.dataset.index = String(line);
        quantityInput.dataset.quantityVariantId = variantId;
      }

      if (removeButton) {
        removeButton.id = `CartDrawer-Remove-${line}`;
        removeButton.dataset.index = String(line);
      }

      if (removeControl) removeControl.dataset.variantId = variantId;
      if (lineError) lineError.id = `CartDrawer-LineItemError-${line}`;
    });
  };

  const updateSubscriptionDataset = (subscription, state, fallbackLine, variantId, sellingPlan) => {
    const items = Array.isArray(state?.items) ? state.items : [];
    const lineIndex = items.findIndex(
      (item) =>
        Number(item?.variant_id) === Number(variantId) &&
        Number(item?.selling_plan_allocation?.selling_plan?.id) === Number(sellingPlan)
    );
    const item = lineIndex >= 0 ? items[lineIndex] : null;
    const line = lineIndex >= 0 ? lineIndex + 1 : fallbackLine;

    if (!subscription || !item || !line) return null;

    subscription.querySelectorAll('[data-dartsrx-cart-subscription-option]').forEach((option) => {
      option.dataset.line = String(line);
      option.dataset.key = item.key || '';
      option.dataset.quantity = String(item.quantity || option.dataset.quantity || 1);

      const optionLinePrice = Number(option.dataset.linePrice);
      const baseTotal = Number(state.total_price) - Number(item.final_line_price);

      if (Number.isFinite(optionLinePrice) && Number.isFinite(baseTotal)) {
        option.dataset.totalPriceText = formatCartMoney(baseTotal + optionLinePrice, state.currency);
      }
    });

    subscription.querySelectorAll('[data-dartsrx-cart-unsubscribe]').forEach((toggle) => {
      toggle.dataset.line = String(line);
      toggle.dataset.key = item.key || '';
      toggle.dataset.quantity = String(item.quantity || toggle.dataset.quantity || 1);
      toggle.dataset.variantId = item.variant_id ? String(item.variant_id) : toggle.dataset.variantId;
    });

    return { item, line };
  };

  const syncSubscriptionState = (button, state, previousLine, previousKey) => {
    const drawer = getDrawer();
    const subscription = button.closest('.dartsrx-cart-subscription');
    const selected = updateSubscriptionDataset(
      subscription,
      state,
      previousLine,
      button.dataset.variantId,
      button.dataset.sellingPlan
    );

    if (!drawer || !selected) return;

    const { item } = selected;
    const cartItems = drawer.querySelector('cart-drawer-items');
    const scrollTop = cartItems?.scrollTop || 0;
    const rows = [...drawer.querySelectorAll('#CartDrawer-CartItems tbody > .cart-item')];
    const row = rows.find((cartRow) => cartRow.dataset.cartItemKey === previousKey) || getCartItemRow(previousLine);
    const money = (value) => formatCartMoney(value, state.currency);

    if (row) {
      row.dataset.cartItemKey = item.key || '';
      row.dataset.variantId = String(item.variant_id || button.dataset.variantId || '');

      row.querySelectorAll('.cart-item__link, .cart-item__name').forEach((link) => {
        if (item.url) link.href = item.url;
      });

      const image = row.querySelector('.cart-item__image');
      if (image && item.image) image.src = item.image;

      const variantValue = row.querySelector('.cart-item__details dl .product-option dd');
      if (variantValue && item.variant_title && item.variant_title !== 'Default Title') {
        variantValue.textContent = item.variant_title;
      }

      const detailPrice =
        row.querySelector('.cart-item__details .cart-item__final-price.product-option') ||
        row.querySelector('.cart-item__details > .product-option');
      const linePrices = [...row.querySelectorAll('.cart-item__price-wrapper .price--end')];
      const sellingPlanText = row.querySelector('.cart-item__details p.product-option');
      const quantityInput = row.querySelector('.quantity__input');

      if (detailPrice) detailPrice.textContent = money(item.final_price);
      if (linePrices.length) linePrices[linePrices.length - 1].textContent = money(item.final_line_price);
      if (sellingPlanText) {
        sellingPlanText.textContent = button.dataset.cadenceLabel || item.selling_plan_allocation?.selling_plan?.name || '';
      }
      if (quantityInput) {
        quantityInput.value = String(item.quantity);
        quantityInput.setAttribute('value', String(item.quantity));
      }

      const tbody = row.closest('tbody');
      const usedRows = new Set();
      const orderedRows = state.items.map((cartItem) => {
        let cartRow = cartItem.key === item.key ? row : rows.find(
          (candidate) =>
            candidate !== row && !usedRows.has(candidate) && candidate.dataset.cartItemKey === cartItem.key
        );

        if (!cartRow) {
          cartRow = rows.find(
            (candidate) =>
              candidate !== row &&
              !usedRows.has(candidate) &&
              Number(candidate.dataset.variantId) === Number(cartItem.variant_id)
          );
        }

        if (cartRow) {
          usedRows.add(cartRow);
          cartRow.dataset.cartItemKey = cartItem.key || '';
          cartRow.dataset.variantId = String(cartItem.variant_id || '');
        }

        return cartRow;
      });

      if (tbody && orderedRows.length === rows.length && orderedRows.every(Boolean)) {
        orderedRows.forEach((cartRow) => tbody.append(cartRow));
      }

      renumberCartRows(drawer);
    }

    const total = drawer.querySelector('.totals__total-value');
    if (total) total.textContent = money(state.total_price);

    if (cartItems) cartItems.scrollTop = scrollTop;

    subscription.querySelectorAll('[data-dartsrx-cart-subscription-option]').forEach((option) => {
      setOptionCurrent(option, option === button);
    });
  };

  const showOptimisticSubscription = (button) => {
    const container = button.closest('.dartsrx-cart-upgrade');
    const template = container?.querySelector('[data-dartsrx-cart-subscription-template]');

    if (!container || !template) return null;

    const fragment = template.content.cloneNode(true);
    const subscription = fragment.querySelector('.dartsrx-cart-subscription');

    container.replaceWith(fragment);
    setMeterComplete(true);
    applyOptimisticLine(button);

    return subscription;
  };

  const setOptionCurrent = (option, isCurrent) => {
    if (isCurrent) {
      option.setAttribute('aria-current', 'true');
    } else {
      option.removeAttribute('aria-current');
    }
  };

  const markSubscriptionOptionActive = (button) => {
    const subscription = button.closest('.dartsrx-cart-subscription');
    const previousActive = subscription?.querySelector('.dartsrx-cart-subscription__option.is-active');
    const options = subscription
      ? [...subscription.querySelectorAll('[data-dartsrx-cart-subscription-option]')]
      : [];

    options.forEach((option) => {
      const isActive = option === button;

      option.classList.toggle('is-active', isActive);
      setOptionCurrent(option, isActive);
    });

    return previousActive;
  };

  const restoreSubscriptionOption = (subscription, previousActive) => {
    if (!subscription || !previousActive) return;

    subscription.querySelectorAll('[data-dartsrx-cart-subscription-option]').forEach((option) => {
      const isActive = option === previousActive;

      option.classList.toggle('is-active', isActive);
      setOptionCurrent(option, isActive);
    });
  };

  const postCartJson = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    });

    const state = await response.json();

    if (!response.ok || state.status) {
      throw new Error(state.description || 'Cart update failed.');
    }

    return state;
  };

  const upgradeLine = async (button) => {
    const line = Number(button.dataset.line);
    const key = button.dataset.key;
    const quantity = Number(button.dataset.quantity || 1);
    const variantId = Number(button.dataset.variantId);
    const sellingPlan = Number(button.dataset.sellingPlan);

    if (!line || !variantId || !sellingPlan) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.classList.add('is-loading');
    button.textContent = 'Applying...';

    try {
      await postCartJson(window.routes?.cart_add_url || '/cart/add.js', {
        items: [
          {
            id: variantId,
            quantity,
            selling_plan: sellingPlan,
          },
        ],
      });

      const removePayload = {
        quantity: 0,
        sections: getSections(),
        sections_url: window.location.pathname,
      };

      if (key) {
        removePayload.id = key;
      } else {
        removePayload.line = line;
      }

      const state = await postCartJson(window.routes?.cart_change_url || '/cart/change.js', removePayload);

      renderDrawer(state);
    } catch (error) {
      console.error(error);

      button.disabled = false;
      button.classList.remove('is-loading');
      button.textContent = originalText || 'Try upgrade again';
    }
  };

  const switchSubscription = async (button) => {
    const line = Number(button.dataset.line);
    const key = button.dataset.key;
    const quantity = Number(button.dataset.quantity || 1);
    const variantId = Number(button.dataset.variantId);
    const sellingPlan = Number(button.dataset.sellingPlan);
    const subscription = button.closest('.dartsrx-cart-subscription');

    if (
      !line ||
      !variantId ||
      !sellingPlan ||
      button.classList.contains('is-active') ||
      subscription?.classList.contains('is-syncing')
    ) {
      return;
    }

    const previousActive = markSubscriptionOptionActive(button);
    const restoreOptimisticLine = applyOptimisticLine(button);

    subscription?.classList.add('is-syncing');
    subscription?.setAttribute('aria-busy', 'true');

    try {
      await postCartJson(window.routes?.cart_add_url || '/cart/add.js', {
        items: [
          {
            id: variantId,
            quantity,
            selling_plan: sellingPlan,
          },
        ],
      });

      const removePayload = {
        quantity: 0,
      };

      if (key) {
        removePayload.id = key;
      } else {
        removePayload.line = line;
      }

      const state = await postCartJson(window.routes?.cart_change_url || '/cart/change.js', removePayload);
      syncSubscriptionState(button, state, line, key);
    } catch (error) {
      console.error(error);
      restoreOptimisticLine();
      restoreSubscriptionOption(subscription, previousActive);
    } finally {
      subscription?.classList.remove('is-syncing');
      subscription?.removeAttribute('aria-busy');
    }
  };

  const unsubscribeLine = async (button) => {
    const line = Number(button.dataset.line);
    const key = button.dataset.key;
    const quantity = Number(button.dataset.quantity || 1);
    const variantId = Number(button.dataset.variantId);
    const originalLabel = button.getAttribute('aria-label');

    if (!line || !variantId) return;

    const subscription = button.closest('.dartsrx-cart-subscription');

    if (subscription?.classList.contains('is-syncing')) return;

    subscription?.classList.add('is-syncing');
    button.disabled = true;
    button.classList.add('is-loading');
    button.setAttribute('aria-label', 'Turning off Subscribe & Save');

    try {
      await postCartJson(window.routes?.cart_add_url || '/cart/add.js', {
        items: [
          {
            id: variantId,
            quantity,
          },
        ],
      });

      const removePayload = {
        quantity: 0,
        sections: getSections(),
        sections_url: window.location.pathname,
      };

      if (key) {
        removePayload.id = key;
      } else {
        removePayload.line = line;
      }

      const state = await postCartJson(window.routes?.cart_change_url || '/cart/change.js', removePayload);

      renderDrawer(state);
    } catch (error) {
      console.error(error);
      subscription?.classList.remove('is-syncing');
      button.disabled = false;
      button.classList.remove('is-loading');
      button.setAttribute('aria-label', originalLabel || 'Turn off Subscribe & Save');
    }
  };

  document.addEventListener('click', (event) => {
    const cartLink = getCartLink(event.target);
    if (cartLink) {
      event.preventDefault();
      openCartDrawer(cartLink);
      return;
    }

    const button = event.target.closest('[data-dartsrx-cart-upgrade]');
    if (button) {
      event.preventDefault();
      upgradeLine(button);
      return;
    }

    const option = event.target.closest('[data-dartsrx-cart-subscription-option]');
    if (option) {
      event.preventDefault();
      switchSubscription(option);
      return;
    }

    const toggle = event.target.closest('[data-dartsrx-cart-unsubscribe]');
    if (!toggle) return;

    event.preventDefault();
    unsubscribeLine(toggle);
  });

  document.addEventListener('keydown', (event) => {
    if (event.code !== 'Space') return;

    const cartLink = getCartLink(event.target);
    if (!cartLink) return;

    event.preventDefault();
    openCartDrawer(cartLink);
  });
})();
